import { User } from "firebase/auth";
import {
  arrayRemove,
  arrayUnion,
  doc,
  getDoc,
  getDocFromCache,
  onSnapshot,
  setDoc,
  writeBatch,
  WriteBatch,
} from "firebase/firestore";
import { UNKNOWN_USER_NAME } from "~/functions/src/v1/const/const";
import { UserDocument } from "~/src/type/firebase/firestore";
import { firestore } from "../firebase/config";
import { updateArticleUserName } from "./analysis";
import { updateFavoritUserName } from "./favorit";
import { COLLECTION_USER } from "./info";

/**
 * ユーザー情報新規作成
 */
export async function createUser(uid: string) {
  console.log("createUser() start");

  //------------
  // 初期データ
  //------------
  const newUserData = {
    name: "no name",
    profile: "",
    favoritList: [],
  };

  //------------
  // 既存判定
  //------------
  const docRef = doc(firestore, COLLECTION_USER, uid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    // ドキュメントが既に存在する場合、登録をスキップ
    console.log("createUser() スキップ: ユーザーは既に存在します");
    return;
  }

  //------------
  // 保存
  //------------
  await setDoc(docRef, newUserData);

  console.log("createUser() 保存完了");
}

/**
 * ユーザー情報取得
 *   1.   キャッシュから取得
 *   2-1. キャッシュから削除済み  → サーバから取得
 *   2-2. キャッシュなし         → サーバから取得
 */
export async function readUser(uid: string): Promise<UserDocument | undefined> {
  console.log("readUser() 開始");

  // 参照
  const docRef = doc(firestore, COLLECTION_USER, uid);
  // 取得データ
  let snapshot;

  try {
    //------------
    // キャッシュから取得
    //------------
    snapshot = await getDocFromCache(docRef);
    if (snapshot.exists()) {
      // データありなら、返して終了
      return snapshot.data() as UserDocument;
    }

    //------------
    // サーバから取得
    //------------
    snapshot = await getDoc(docRef);
    return snapshot.data() as UserDocument;

    // キャッシュにない場合は、エラーが発生するため、catch()を実装
  } catch (e) {
    console.log("readUser() キャッシュなしのため、サーバから取得する e:", e);

    //------------
    // サーバから取得
    //------------
    snapshot = await getDoc(docRef);
    return snapshot.data() as UserDocument;
  }
}

/**
 * ユーザー名取得
 *
 * @note 指定されたUIDのユーザー名を取得する
 */
export async function getUserName(uid: string): Promise<string> {
  const userName = UNKNOWN_USER_NAME;

  try {
    //------------
    // ユーザー情報
    //------------
    // 記事中の投稿者IDと、ユーザーコレクションのドキュメントIDが一致するドキュメントを取得
    const docRef = doc(firestore, COLLECTION_USER, uid);
    const snapshot = await getDoc(docRef);

    // 投稿者のユーザー情報があれば、ユーザー名を取得
    if (snapshot.exists()) {
      const docData = snapshot.data();
      return docData.name;
    }
  } catch (e) {
    console.log("getUserName e=", e);
  }

  //------------
  // エラー
  //------------
  return userName;
}

/**
 * ユーザー情報更新
 * @note ユーザー名、プロフィールのみ更新
 */
export async function updateUserProfile(
  user: User,
  currentInfo: UserDocument,
  info: {
    name: string;
    profile: string;
  }
) {
  const { uid } = user;

  const batch = writeBatch(firestore);

  //------------
  // ユーザープロフィールを更新
  //------------
  const userRef = doc(firestore, COLLECTION_USER, uid);
  batch.update(userRef, {
    name: info.name,
    profile: info.profile,
  });

  //------------
  // 非同期データの更新
  //------------
  // ユーザー名を変更している場合
  if (info.name !== currentInfo.name) {
    // 分析記事コレクション
    await updateArticleUserName(batch, uid, info.name);

    // いいねコレクション
    await updateFavoritUserName(batch, uid, info.name);
  }

  // コミット
  await batch.commit();

  console.log("updateUserProfile 更新完了 info.name=", info.name + " info.profile" + info.profile);
}

/**
 * ユーザーの付与したいいねの分析記事IDを取得
 *
 * @note 未使用
 */
export async function readUserFavoritList(user: User | null | undefined): Promise<string[]> {
  if (!user) {
    return [];
  }

  try {
    const { uid } = user;

    const userRef = doc(firestore, COLLECTION_USER, uid);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    if (!userData) {
      console.log("ユーザーコレクションにユーザーデータなし");
      return [];
    }

    return userData.favoritList;
  } catch (e) {
    console.error("Error readUserFavoritList() : ", e);
    return [];
  }
}

/**
 * ユーザーのいいねリストへ記事の追加
 *
 * @note batch.update(1, 2)へ渡すデータを返す。
 *       １. 更新対象のドキュメント参照
 *       ２ .更新データ（リスト追加）
 */
export function updateAddUserFavoritList(batch: WriteBatch, uid: string, analysisId: string) {
  // 参照
  const userRef = doc(firestore, COLLECTION_USER, uid);
  // いいねリスト追加データ
  const addData = { favoritList: arrayUnion(analysisId) };

  batch.update(userRef, addData);
}

/**
 * ユーザーのいいねリストから記事を削除
 *
 * @note batch.update(1, 2)へ渡すデータを返す。
 *       １. 更新対象のドキュメント参照
 *       ２ .更新データ（リスト削除）
 */
export function updateRemoveUserFavoritList(batch: WriteBatch, uid: string, analysisId: string) {
  //------------
  // データ生成
  //------------
  // 参照
  const userRef = doc(firestore, COLLECTION_USER, uid);
  // いいねリスト削除データ
  const addData = { favoritList: arrayRemove(analysisId) };

  batch.update(userRef, addData);
}

/**
 * リアルタイムリスナー登録
 */
export function setUserRealtimeListerner(
  uid: string,
  func: (_userInfo: UserDocument) => undefined
) {
  // 監視対象
  const ref = doc(firestore, COLLECTION_USER, uid);

  //------------
  // リアルタイムリスナー生成
  //------------
  const unsubscribe = onSnapshot(ref, (snapshot) => {
    console.log("変更を検知:", snapshot.data());
    console.log("変更を検知 hasPendingWrites:", snapshot.metadata.hasPendingWrites);

    //------------
    // サーバ反映判定
    //------------
    if (!snapshot.metadata.hasPendingWrites) {
      // サーバに反映されていないなら、処理しない
      return;
    }

    // 渡された処理を実行
    func(snapshot.data() as UserDocument);

    // リスナー解除
    unsubscribe();
  });
}
